import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ShieldAlert,
  Users,
  Trophy,
  Swords,
  Grid3X3,
  Trash2,
  RefreshCw,
  Key,
  LogOut,
  AlertTriangle,
} from 'lucide-react';

const SUPER_ADMIN_KEY_STORAGE = 'rallydesk_super_admin_key';

function formatDate(value) {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function getSuperAdminHeaders() {
  const token = localStorage.getItem('rallydesk_token');
  const key = sessionStorage.getItem(SUPER_ADMIN_KEY_STORAGE);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  if (key) headers['X-Super-Admin-Key'] = key;
  return headers;
}

const SuperAdmin = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [keyInput, setKeyInput] = useState('');
  const [keyVerified, setKeyVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState({ items: [], total: 0 });
  const [tournaments, setTournaments] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [cleanupDays, setCleanupDays] = useState(30);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [orphanDays, setOrphanDays] = useState(7);
  const [orphanPreview, setOrphanPreview] = useState(null);
  const [orphanCleanupRunning, setOrphanCleanupRunning] = useState(false);

  const hasKeyInSession = !!sessionStorage.getItem(SUPER_ADMIN_KEY_STORAGE);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
      return;
    }
  }, [user, authLoading, navigate]);

  const verifyKey = useCallback(async () => {
    if (!keyInput.trim()) {
      toast.error('Enter the super admin key');
      return;
    }
    setVerifying(true);
    try {
      sessionStorage.setItem(SUPER_ADMIN_KEY_STORAGE, keyInput.trim());
      const { data } = await axios.get(`${API_URL}/super-admin/verify`, {
        headers: getSuperAdminHeaders(),
      });
      if (data?.ok) {
        setKeyVerified(true);
        toast.success('Key accepted');
      }
    } catch (err) {
      sessionStorage.removeItem(SUPER_ADMIN_KEY_STORAGE);
      const msg = err.response?.data?.detail || 'Invalid key or access denied';
      toast.error(msg);
    } finally {
      setVerifying(false);
    }
  }, [keyInput]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/super-admin/stats`, {
        headers: getSuperAdminHeaders(),
      });
      setStats(data);
    } catch (e) {
      if (e.response?.status === 403 || e.response?.status === 503) {
        sessionStorage.removeItem(SUPER_ADMIN_KEY_STORAGE);
        setKeyVerified(false);
        toast.error(e.response?.data?.detail || 'Session expired');
      } else {
        toast.error('Failed to load stats');
      }
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/super-admin/users?limit=100`, {
        headers: getSuperAdminHeaders(),
      });
      setUsers(data);
    } catch (e) {
      if (e.response?.status === 403 || e.response?.status === 503) {
        sessionStorage.removeItem(SUPER_ADMIN_KEY_STORAGE);
        setKeyVerified(false);
        toast.error(e.response?.data?.detail || 'Session expired');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTournaments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/super-admin/tournaments?limit=100`, {
        headers: getSuperAdminHeaders(),
      });
      setTournaments({ items: data?.items ?? [], total: data?.total ?? 0 });
    } catch (e) {
      if (e.response?.status === 403 || e.response?.status === 503) {
        sessionStorage.removeItem(SUPER_ADMIN_KEY_STORAGE);
        setKeyVerified(false);
        toast.error(e.response?.data?.detail || 'Session expired');
      } else {
        const msg = e.response?.data?.detail || e.message || 'Failed to load tournaments';
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // When portal is visible (key just verified OR key already in session e.g. after refresh), load stats and tournaments
  useEffect(() => {
    if (hasKeyInSession) {
      fetchStats();
      fetchTournaments();
    }
  }, [hasKeyInSession, fetchStats, fetchTournaments]);

  const handleLogoutSuperAdmin = () => {
    sessionStorage.removeItem(SUPER_ADMIN_KEY_STORAGE);
    setKeyVerified(false);
    setKeyInput('');
    toast.info('Super admin key cleared');
  };

  const handleDeleteTournament = async (tournamentId, name) => {
    if (!window.confirm(
      `Delete tournament "${name}"?\n\nThis will permanently remove all related data: matches (games), players, teams, divisions, competitions, and resources. Those games and players will no longer be available. This cannot be undone.`
    )) return;
    try {
      await axios.delete(`${API_URL}/super-admin/tournaments/${tournamentId}`, {
        headers: getSuperAdminHeaders(),
      });
      toast.success('Tournament deleted');
      fetchTournaments();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleCleanupDraft = async () => {
    setCleanupRunning(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/super-admin/cleanup/draft-tournaments?older_than_days=${cleanupDays}`,
        {},
        { headers: getSuperAdminHeaders() }
      );
      toast.success(data?.message || 'Cleanup done');
      fetchStats();
      fetchTournaments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cleanup failed');
    } finally {
      setCleanupRunning(false);
    }
  };

  const handleOrphanPreview = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/super-admin/cleanup/orphan-users/preview?older_than_days=${orphanDays}`,
        { headers: getSuperAdminHeaders() }
      );
      setOrphanPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    }
  };

  const handleCleanupOrphanUsers = async () => {
    if (!window.confirm(
      `Delete users who have no tournaments (owner or moderator) and were created more than ${orphanDays} days ago? This cannot be undone.`
    )) return;
    setOrphanCleanupRunning(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/super-admin/cleanup/orphan-users?older_than_days=${orphanDays}`,
        {},
        { headers: getSuperAdminHeaders() }
      );
      toast.success(data?.message || 'Cleanup done');
      setOrphanPreview(null);
      fetchStats();
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cleanup failed');
    } finally {
      setOrphanCleanupRunning(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  if (!keyVerified && !hasKeyInSession) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-2 border-amber-500/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="w-6 h-6 text-amber-500" />
              Super Admin Portal
            </CardTitle>
            <CardDescription>
              Enter the super admin key to manage the system. The key is configured on the server (SUPER_ADMIN_SECRET).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="super-admin-key">Super Admin Key</Label>
              <Input
                id="super-admin-key"
                type="password"
                placeholder="Enter key"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && verifyKey()}
                autoComplete="off"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={verifyKey} disabled={verifying} className="flex-1">
                <Key className="w-4 h-4 mr-2" />
                {verifying ? 'Verifying...' : 'Unlock'}
              </Button>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6 space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-2">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase flex items-center gap-2">
              <ShieldAlert className="w-8 h-8 text-amber-500" />
              Super Admin Portal
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              System management and cleanup. Key is stored in this session only.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogoutSuperAdmin}>
            <LogOut className="w-4 h-4 mr-2" />
            Clear key & lock
          </Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Users className="w-8 h-8 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Users</p>
                <p className="text-2xl font-bold">{stats.users}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Trophy className="w-8 h-8 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Tournaments</p>
                <p className="text-2xl font-bold">{stats.tournaments}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Swords className="w-8 h-8 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Competitions</p>
                <p className="text-2xl font-bold">{stats.competitions}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Grid3X3 className="w-8 h-8 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Matches</p>
                <p className="text-2xl font-bold">{stats.matches}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Users className="w-8 h-8 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Players (tourn.)</p>
                <p className="text-2xl font-bold">{stats.players}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="tournaments" className="w-full">
        <TabsList>
          <TabsTrigger value="tournaments" onClick={fetchTournaments}>Tournaments</TabsTrigger>
          <TabsTrigger value="users" onClick={fetchUsers}>Users</TabsTrigger>
          <TabsTrigger value="cleanup">Cleanup</TabsTrigger>
        </TabsList>
        <TabsContent value="tournaments" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>All Tournaments</CardTitle>
                <CardDescription>Delete tournaments and all related data. When a tournament is deleted, all its matches (games), players, teams, divisions, competitions, and resources are permanently removed; those options will no longer be available.</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchTournaments} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {loading && (!tournaments.items || tournaments.items.length === 0) ? (
                <p className="text-muted-foreground py-4 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Loading tournaments…
                </p>
              ) : !tournaments.items || tournaments.items.length === 0 ? (
                <p className="text-muted-foreground py-4">No tournaments. Click Refresh to load.</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {tournaments.items.map((t) => (
                    <div
                      key={t.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/40 border"
                    >
                      <div>
                        <p className="font-medium">{t.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {t.id || t._id || '—'} · {t.status} · {formatDate(t.created_at)}
                        </p>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteTournament(t.id || t._id, t.name)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="users" className="mt-4 space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Users</CardTitle>
                <CardDescription>All registered users and tournaments they manage (owner or moderator).</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchUsers} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {users.items.length === 0 && !loading ? (
                <p className="text-muted-foreground py-4">No users. Click Refresh to load.</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {users.items.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/40 border gap-4"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{u.display_name || u.username || u.email || u.id}</p>
                        <p className="text-xs text-muted-foreground">
                          {u.email} · {u.role} · {formatDate(u.created_at)}
                        </p>
                        {u.managed_tournaments?.length > 0 ? (
                          <p className="text-xs text-muted-foreground mt-1">
                            Manages {u.managed_tournaments.length} tournament(s): {u.managed_tournaments.map((t) => t.name).join(', ')}
                          </p>
                        ) : (
                          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">No tournaments</p>
                        )}
                      </div>
                      <Badge variant="outline">{u.role}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-amber-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-500" />
                Cleanup users with no tournaments
              </CardTitle>
              <CardDescription>
                Delete users who have never created a tournament and are not moderators of any (e.g. bot accounts). Only users created more than N days ago are considered.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4 flex-wrap">
                <Label htmlFor="orphan-days">Only users created more than (days) ago</Label>
                <Input
                  id="orphan-days"
                  type="number"
                  min={1}
                  max={365}
                  value={orphanDays}
                  onChange={(e) => { setOrphanDays(parseInt(e.target.value, 10) || 7); setOrphanPreview(null); }}
                  className="w-24"
                />
                <Button variant="outline" size="sm" onClick={handleOrphanPreview}>
                  Preview
                </Button>
              </div>
              {orphanPreview !== null && (
                <div className="rounded-lg bg-muted/50 p-3 text-sm">
                  <p className="font-medium">{orphanPreview.count} user(s) would be deleted.</p>
                  {orphanPreview.sample?.length > 0 && (
                    <p className="text-muted-foreground mt-1">Sample: {orphanPreview.sample.map(u => u.email || u.username || u.id).join(', ')}</p>
                  )}
                </div>
              )}
              <Button
                variant="destructive"
                onClick={handleCleanupOrphanUsers}
                disabled={orphanCleanupRunning}
              >
                {orphanCleanupRunning ? 'Running...' : `Delete users with no tournaments (created > ${orphanDays} days ago)`}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="cleanup" className="mt-4">
          <Card className="border-amber-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Cleanup Draft Tournaments
              </CardTitle>
              <CardDescription>
                Permanently delete draft tournaments older than the specified number of days. Use with caution.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Label htmlFor="cleanup-days">Delete drafts older than (days)</Label>
                <Input
                  id="cleanup-days"
                  type="number"
                  min={1}
                  max={365}
                  value={cleanupDays}
                  onChange={(e) => setCleanupDays(parseInt(e.target.value, 10) || 30)}
                  className="w-24"
                />
              </div>
              <Button
                variant="destructive"
                onClick={handleCleanupDraft}
                disabled={cleanupRunning}
              >
                {cleanupRunning ? 'Running...' : `Delete draft tournaments older than ${cleanupDays} days`}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SuperAdmin;
